import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type IMergedSlot, type IPuEventChecklistResponse, type IPuEventQueueItem, UNIT_CANCELLED } from '../interface/pu-events';
import { getPuEventChecklist, recordPuEvent } from '../lib/pu-events-api';
import { mergeChecklist, staleQueueItems } from '../lib/pu-events-reconcile';
import {
	readChecklistCache,
	readQueue,
	removeQueueItem,
	subscribePuEventsChanged,
	upsertQueueItem,
	writeChecklistCache,
} from '../lib/pu-events-store';

export type ChecklistLoad = 'idle' | 'loading' | 'ready' | 'offline' | 'error';

export interface IPulseChecklist {
	slots: IMergedSlot[];
	capturedCount: number;
	load: ChecklistLoad;
	queueLength: number;
	refresh: () => Promise<void>;
	reloadQueue: () => void;
	/** Optimistically mark a slot captured after a confirmed write (no flicker while the refetch lands). */
	applyLocalConfirm: (item: IPuEventQueueItem, mediaUrls?: string[]) => void;
	/** Re-send every queued (pending/failed) capture sequentially. */
	flushQueue: () => Promise<void>;
}

/**
 * Local-first checklist state for the Election Day Pulse: paints instantly
 * from the localStorage cache + queue, reconciles against the server
 * get-checklist read, and auto-flushes the offline queue on reconnect.
 */
export const usePulseChecklist = (
	electionId: number | undefined,
	puCode: string | undefined,
	options: {
		/**
		 * Set while the unit is locked (INEC cancelled it). Queued captures from before this moment
		 * still send — a milestone that happened before the cancellation is true — later ones are
		 * dropped, since the server refuses them.
		 */
		cancelledAt?: string;
	} = {}
): IPulseChecklist => {
	const cancelledAtMs = options.cancelledAt ? Date.parse(options.cancelledAt) : NaN;
	const ready = !!electionId && !!puCode;
	const [server, setServer] = useState<IPuEventChecklistResponse | null>(() =>
		ready ? readChecklistCache(electionId, puCode) : null
	);
	const [queue, setQueue] = useState<IPuEventQueueItem[]>(() => (ready ? readQueue(electionId, puCode) : []));
	const [load, setLoad] = useState<ChecklistLoad>('idle');
	const flushingRef = useRef(false);

	// Mirror of `server` for callbacks that must not run inside a setState updater.
	const serverRef = useRef(server);
	useEffect(() => {
		serverRef.current = server;
	});

	const reloadQueue = useCallback(() => {
		if (!ready) return;
		setQueue(readQueue(electionId, puCode));
	}, [ready, electionId, puCode]);

	const refresh = useCallback(async () => {
		if (!ready) return;
		setLoad((prev) => (prev === 'ready' ? 'ready' : 'loading'));
		const outcome = await getPuEventChecklist(electionId, puCode);
		if (!outcome.reachable) {
			setLoad((prev) => (prev === 'ready' ? 'ready' : 'offline'));
			return;
		}
		if (outcome.httpStatus >= 200 && outcome.httpStatus < 300 && outcome.body?.slots?.length) {
			setServer(outcome.body);
			writeChecklistCache(electionId, puCode, outcome.body);
			// Drop queue items the server has already confirmed (e.g. a flush
			// from another tab/session won the race).
			const stale = staleQueueItems(outcome.body, readQueue(electionId, puCode));
			stale.forEach((item) => removeQueueItem(electionId, puCode, item.referenceId));
			if (stale.length) setQueue(readQueue(electionId, puCode));
			setLoad('ready');
			return;
		}
		setLoad((prev) => (prev === 'ready' ? 'ready' : 'error'));
	}, [ready, electionId, puCode]);

	const applyLocalConfirm = useCallback(
		(item: IPuEventQueueItem, mediaUrls?: string[]) => {
			if (!ready) return;
			const prev = serverRef.current ?? readChecklistCache(electionId, puCode);
			if (prev?.slots?.length) {
				const next: IPuEventChecklistResponse = {
					...prev,
					slots: prev.slots.map((slot) =>
						slot.eventType === item.request.eventType
							? {
									...slot,
									captured: true,
									occurredAt: slot.captured ? slot.occurredAt : item.request.occurredAt,
									location: slot.captured ? slot.location : item.request.location,
									accuracy: slot.captured ? slot.accuracy : item.request.accuracy,
									mediaUrls: mediaUrls ?? (slot.captured ? slot.mediaUrls : item.request.mediaUrls),
									referenceId: slot.captured ? slot.referenceId : item.referenceId,
								}
							: slot
					),
				};
				// Persisting also notifies every other checklist instance (widget/panel).
				writeChecklistCache(electionId, puCode, next);
				setServer(next);
			}
			setQueue(readQueue(electionId, puCode));
		},
		[ready, electionId, puCode]
	);

	const flushQueue = useCallback(async () => {
		if (!ready || flushingRef.current) return;
		if (typeof navigator !== 'undefined' && !navigator.onLine) return;
		flushingRef.current = true;
		try {
			// Sequential on purpose — no server-side race between captures.
			const items = readQueue(electionId, puCode).filter((i) => i.status === 'pending' || i.status === 'failed');
			let confirmedAny = false;
			for (const item of items) {
				if (Number.isFinite(cancelledAtMs) && Date.parse(item.request.occurredAt) >= cancelledAtMs) {
					removeQueueItem(electionId, puCode, item.referenceId);
					continue;
				}
				upsertQueueItem(electionId, puCode, { ...item, status: 'sending' });
				setQueue(readQueue(electionId, puCode));
				const outcome = await recordPuEvent(item.request);
				if (outcome.reachable && outcome.httpStatus >= 200 && outcome.httpStatus < 300) {
					removeQueueItem(electionId, puCode, item.referenceId);
					applyLocalConfirm(item);
					confirmedAny = true;
				} else if (outcome.reachable && outcome.errorCode === UNIT_CANCELLED) {
					// Terminal: the unit is cancelled. Retrying can never succeed.
					removeQueueItem(electionId, puCode, item.referenceId);
				} else if (outcome.reachable) {
					upsertQueueItem(electionId, puCode, { ...item, status: 'failed', retryCount: item.retryCount + 1 });
				} else {
					// Network gone again mid-flush — back to pending, stop.
					upsertQueueItem(electionId, puCode, { ...item, status: 'pending' });
					break;
				}
			}
			setQueue(readQueue(electionId, puCode));
			if (confirmedAny) void refresh();
		} finally {
			flushingRef.current = false;
		}
	}, [ready, electionId, puCode, applyLocalConfirm, refresh, cancelledAtMs]);

	// Initial fetch + flush; re-run if the assignment identity changes.
	useEffect(() => {
		if (!ready) return;
		// A leftover 'armed' item means the app died inside the undo grace
		// window — the tap was never undone, so promote it to 'pending' and let
		// the flush commit it.
		const stale = readQueue(electionId, puCode).filter((i) => i.status === 'armed');
		stale.forEach((i) => upsertQueueItem(electionId, puCode, { ...i, status: 'pending' }));
		setServer(readChecklistCache(electionId, puCode));
		setQueue(readQueue(electionId, puCode));
		void refresh();
		void flushQueue();
	}, [ready, electionId, puCode]);

	// Auto-flush when connectivity returns.
	useEffect(() => {
		if (!ready) return;
		const handleOnline = () => {
			void flushQueue();
		};
		window.addEventListener('online', handleOnline);
		return () => window.removeEventListener('online', handleOnline);
	}, [ready, flushQueue]);

	// Stay in sync with the OTHER checklist instance on the page (widget vs
	// panel): any queue/cache write re-hydrates this instance's state too.
	useEffect(() => {
		if (!ready) return;
		return subscribePuEventsChanged(electionId, puCode, () => {
			setServer(readChecklistCache(electionId, puCode));
			setQueue(readQueue(electionId, puCode));
		});
	}, [ready, electionId, puCode]);

	const slots = useMemo(() => mergeChecklist(server, queue), [server, queue]);
	const capturedCount = useMemo(() => slots.filter((s) => s.status === 'captured' || s.status === 'syncing').length, [slots]);

	return {
		slots,
		capturedCount,
		load,
		// Only items genuinely awaiting a (re)send — 'armed' is still inside the
		// undo grace window and 'sending' is already in flight.
		queueLength: queue.filter((i) => i.status === 'pending' || i.status === 'failed').length,
		refresh,
		reloadQueue,
		applyLocalConfirm,
		flushQueue,
	};
};
